/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Needed for redux-saga es6 generator support
// import '@babel/polyfill';

// Import all the third party stuff
import React from 'react';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import history from './config/utils/history';
import 'sanitize.css/sanitize.css';
import './index.css';
// Import root app
import App from './app/containers/App';

// Upgrade to React 18.0 test - ReactDOM.render is no longer supported in React 18
// Instructions from: https://reactjs.org/blog/2022/03/08/react-18-upgrade-guide.html#updates-to-client-rendering-apis
import { createRoot } from 'react-dom/client';

// Import icons
import LanguageProvider from './app/containers/LanguageProvider';
import { icons } from './app/assets/icons';
import { ControlFeedbackProvider } from './app/components/ControlFeedbackSnackbar/ControlFeedbackContext';
import ControlFeedbackContainer from './app/components/ControlFeedbackSnackbar/ControlFeedbackContainer';

// Import Language Provider

// Load the favicon and the .htaccess file
// import '!file-loader?name=[name].[ext]!./app/images/favicon.ico';
// import 'file-loader?name=.htaccess!./app/.htaccess'; // eslint-disable-line import/extensions-

// Import InfoProvider for badges
import { InfoProvider } from './app/infocontext/InfoContext';

import configureStore from './app/configureStore';

// Import i18n messages
import { translationMessages } from './app/i18n';
import { CssBaseline } from '@mui/material';
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
import { useGLTF } from '@react-three/drei';

const kioskMode = new URLSearchParams(window.location.search).get('kiosk');
if (kioskMode === '1') window.localStorage.setItem('kiosk', '1');
else if (kioskMode === '0') window.localStorage.removeItem('kiosk');

// Create redux store with history
const initialState = {};

const store = configureStore(initialState, history);
const MOUNT_NODE = document.getElementById('app');

useGLTF.setDecoderPath('/draco/');

React.icons = icons;

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse" />
  </div>
);

const theme = createTheme({
  typography: {
    fontFamily: 'Poppins, Arial, sans-serif',
    h1: {
      fontFamily: 'Poppins, Arial, sans-serif',
      fontWeight: 700
    },
    h2: {
      fontFamily: 'Poppins, Arial, sans-serif',
      fontWeight: 600
    },
    h3: {
      fontFamily: 'Poppins, Arial, sans-serif',
      fontWeight: 500
    },
    h4: {
      fontFamily: 'Poppins, Arial, sans-serif',
      fontWeight: 500
    },
    h5: {
      fontFamily: 'Poppins, Arial, sans-serif',
      fontWeight: 400
    },
    h6: {
      fontFamily: 'Poppins, Arial, sans-serif',
      fontWeight: 400
    },
    body1: {
      fontFamily: 'Poppins, Arial, sans-serif',
      fontWeight: 400
    },
    body2: {
      fontFamily: 'Poppins, Arial, sans-serif',
      fontWeight: 400
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: (themeParam) => ({
        ':root': {
          '--z-index-modal': String(themeParam.zIndex.modal),
          '--z-index-hud': String(themeParam.zIndex.modal - 1),
          '--z-index-overlay': String(themeParam.zIndex.modal + 1),
          '--z-index-modal-content': String(themeParam.zIndex.modal + 2),
          '--z-index-header-dropdown': String(themeParam.zIndex.modal + 3)
        }
      })
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme: muiTheme }) => ({
          '&.Mui-focused': {
            color: muiTheme.palette.ledinekOrange.main
          }
        })
      }
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
        variant: 'outlined'
      }
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined'
      }
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
        variant: 'outlined'
      },
      styleOverrides: {
        select: {
          display: 'flex',
          alignItems: 'center'
        },
        icon: ({ theme: muiTheme }) => ({
          color: muiTheme.palette.text.secondary
        })
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: 4,
          maxHeight: 360,
          border: '1px solid #e1e4e8',
          borderRadius: 6,
          boxShadow: '0 8px 20px rgb(0 0 0 / 14%)'
        },
        list: {
          padding: 0
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme: muiTheme }) => ({
          '.MuiMenu-list[role="listbox"] &': {
            minHeight: 44,
            padding: '10px 16px 10px 19px',
            borderBottom: `1px solid ${muiTheme.palette.divider}`,
            '&:last-of-type': {
              borderBottom: 0
            },
            '&.Mui-selected': {
              boxShadow: `inset 3px 0 ${muiTheme.palette.ledinekOrange.main}`,
              backgroundColor: 'rgba(247, 195, 21, 0.12)'
            },
            '&.Mui-selected:hover': {
              backgroundColor: 'rgba(247, 195, 21, 0.18)'
            }
          }
        })
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme: muiTheme }) => ({
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: muiTheme.palette.ledinekOrange.main
          }
        })
      }
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        variant: 'contained',
        width: '100%'
      },
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'none',
          '&:hover': {
            backgroundColor: '#7a7979ff',
            color: '#fff'
          }
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#9aa0a6',
          '&.Mui-checked': {
            color: '#0c963a'
          }
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: '#1976d2',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.08)'
            }
          },
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: 'rgba(25, 118, 210, 0.5)',
            opacity: 1
          }
        }
      }
    }
  },
  palette: {
    primary: {
      main: '#ffffffff',
      contrastText: '#000000ff'
    },
    secondary: {
      main: '#616263ff'
    },
    mui: {
      main: '#1976d2',
      hover: 'rgba(25, 118, 210, 0.08)',
      track: 'rgba(25, 118, 210, 0.5)'
    },
    normal: {
      main: '#636f83',
      contrastText: '#ffffff'
    },
    dark: {
      main: '#000000',
      contrastText: '#ffffff'
    },
    error: {
      main: '#dc3545'
    },
    warning: {
      main: '#ffc107'
    },
    info: {
      main: '#17a2b8'
    },
    success: {
      main: '#28a745'
    },
    ledinekOrange: {
      main: '#f7c315'
    }
  },
  zIndex: {
    modal: 1100
  },
  spacing: 8,
  alignItems: 'center',
  justifyContent: 'center'
});

// Upgrade to React 18.0 test - ReactDOM.render is no longer supported in React 18
const root = createRoot(MOUNT_NODE);

const render = (messages) => {
  root.render(
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <Provider store={store}>
          <CssBaseline />
          <LanguageProvider messages={messages}>
            <InfoProvider>
              <ControlFeedbackProvider>
                <Router history={history}>
                  <React.Suspense fallback={loading}>
                    <App />
                  </React.Suspense>
                </Router>
                <ControlFeedbackContainer />
              </ControlFeedbackProvider>
            </InfoProvider>
          </LanguageProvider>
        </Provider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

if (module.hot) {
  // Hot reloadable React components and translation json files
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
  module.hot.accept(['./app/i18n', './app/containers/App'], () => {
    // Upgrade to React 18.0 test - ReactDOM.render is no longer supported in React 18
    root.unmount();
    render(translationMessages);
  });
}

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  new Promise((resolve) => {
    resolve(import('intl'));
  })
    .then(() =>
      Promise.all([
        import('intl/locale-data/jsonp/en.js'),
        import('intl/locale-data/jsonp/de.js'),
        import('intl/locale-data/jsonp/pt.js'),
        import('intl/locale-data/jsonp/es.js'),
        import('intl/locale-data/jsonp/he.js'),
        import('intl/locale-data/jsonp/fi.js')
      ])
    )
    .then(() => render(translationMessages))
    .catch((err) => {
      throw err;
    });
} else {
  render(translationMessages);
}

// Install ServiceWorker and AppCache in the end since
// it's not most important operation and if main code fails,
// we do not want it installed
if (process.env.NODE_ENV === 'production') {
  require('offline-plugin/runtime').install(); // eslint-disable-line global-require
} else {
  // console.log('process.env.NODE_ENV: ', process.env.NODE_ENV);
  // console.log('process.env: ', process.env);
}
